// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useMemo, useState } from 'react'
import { formatUsTimestamp } from '../../api/format'
import { MessageSquare, Loader2, AlertCircle, Filter } from 'lucide-react'
import * as api from '../../api/client'
import { usePaged, Pager, DEFAULT_PAGE_SIZE } from '../../components/Pager'
import { friendlyError } from '../../api/errors'

const STATUS_FILTERS: { value: string; label: string }[] = [
  { value: '',         label: 'All' },
  { value: 'new',      label: 'New' },
  { value: 'triaged',  label: 'Triaged' },
  { value: 'linked',   label: 'Linked' },
  { value: 'closed',   label: 'Closed' },
]

/**
 * /admin/feedback — read + triage feedback submitted from the in-product
 * feedback widget. Backend: GET /api/feedback, POST /api/feedback/{id}/triage.
 *
 * For Sprint 11 we render the inbox + per-row triage actions inline.
 * Deeper triage flows (linking to roadmap items) can come later.
 */
export default function FeedbackTab() {
  const [items, setItems] = useState<api.FeedbackItem[]>([])
  const [counts, setCounts] = useState<api.FeedbackInboxCounts | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [busyId, setBusyId] = useState<string | null>(null)

  async function reload() {
    setLoading(true); setError(null)
    try {
      const r = await api.listFeedback({ status: statusFilter || undefined, limit: 100 })
      setItems(r.items)
      setCounts(r.counts)
    } catch (e) {
      setError(friendlyError(e))
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => { reload() }, [statusFilter])

  async function setStatus(id: string, status: api.TriageInput['status']) {
    setBusyId(id); setError(null)
    try {
      await api.triageFeedback(id, { status })
      await reload()
    } catch (e) {
      setError(friendlyError(e))
    } finally {
      setBusyId(null)
    }
  }

  const filteredCount = useMemo(() => items.length, [items])

  // Reset to page 1 when the status filter changes (a length resetKey misses two
  // different result sets that happen to have the same row count).
  const { page, setPage, pageSize, setPageSize, pageRows, total, totalPages } = usePaged(items, DEFAULT_PAGE_SIZE, statusFilter)

  return (
    <div>
      <div style={headerRowStyle}>
        <div>
          <h2 style={h2Style}><MessageSquare size={16} aria-hidden="true" /> Feedback inbox</h2>
          <p style={subtitleStyle}>
            User-submitted feedback from the in-product widget. Triage to mark as reviewed,
            link to a roadmap item, or close.
          </p>
        </div>
        {counts && (
          <div style={countsStyle}>
            <Counter label="Total"   value={counts.total} />
            <Counter label="New"     value={counts.status_new} tone="warn" />
            <Counter label="Triaged" value={counts.triaged} />
            <Counter label="Linked"  value={counts.linked} tone="ok" />
            <Counter label="Closed"  value={counts.closed} muted />
          </div>
        )}
      </div>

      <div style={filterRowStyle}>
        <Filter size={13} aria-hidden="true" style={{ color: 'var(--color-text-mute)' }} />
        {STATUS_FILTERS.map(f => (
          <button
            key={f.value || 'all'}
            onClick={() => setStatusFilter(f.value)}
            style={{ ...chipStyle, ...(statusFilter === f.value ? chipActiveStyle : null) }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {error && <div style={errorBox}><AlertCircle size={14} /> {error}</div>}

      {loading ? (
        <div style={loadingBox}><Loader2 size={16} className="spin" /> Loading…</div>
      ) : filteredCount === 0 ? (
        <div style={emptyBox}>No feedback in this view.</div>
      ) : (
        <>
        <Pager page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPage={setPage} onPageSize={setPageSize} label="items" />
        <ul style={listStyle}>
          {pageRows.map(it => (
            <FeedbackRow
              key={it.id}
              item={it}
              busy={busyId === it.id}
              onSetStatus={(s) => setStatus(it.id, s)}
            />
          ))}
        </ul>
        <Pager page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPage={setPage} showPageSize={false} label="items" />
        </>
      )}
    </div>
  )
}

function FeedbackRow({ item, busy, onSetStatus }: {
  item: api.FeedbackItem
  busy: boolean
  onSetStatus: (s: api.TriageInput['status']) => void
}) {
  return (
    <li style={rowStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={metaLineStyle}>
            <StatusPill status={item.status} />
            <span style={pillCat}>{item.category}</span>
            {item.severity && <span style={pillCat}>{item.severity}</span>}
            <span style={pillScreen}>{item.screen}</span>
            <span style={timeStyle}>{formatTimestamp(item.submitted_at)}</span>
          </div>
          <p style={bodyStyle}>{item.message}</p>
          <div style={subMetaStyle}>
            <span className="email-text">From: <strong>{item.submitter_email ?? 'anonymous'}</strong></span>
            {item.submitter_role && <span>· {item.submitter_role}</span>}
            {item.viewport && <span>· {item.viewport}</span>}
          </div>
          {item.linked_roadmap_title && (
            <div style={linkedStyle}>
              ↳ Linked to roadmap: <strong>{item.linked_roadmap_title}</strong>
            </div>
          )}
          {item.triage_notes && (
            <div style={notesStyle}>
              Triage notes: {item.triage_notes}
            </div>
          )}
        </div>
        <div style={actionsStyle}>
          {item.status !== 'triaged' && item.status !== 'linked' && (
            <button onClick={() => onSetStatus('triaged')} disabled={busy} style={btnStyle}>
              {busy ? <Loader2 size={11} className="spin" /> : 'Mark triaged'}
            </button>
          )}
          {item.status !== 'closed' && (
            <button onClick={() => onSetStatus('closed')} disabled={busy} style={btnStyleSecondary}>
              Close
            </button>
          )}
          {item.status === 'closed' && (
            <button onClick={() => onSetStatus('new')} disabled={busy} style={btnStyleSecondary}>
              Reopen
            </button>
          )}
        </div>
      </div>
    </li>
  )
}

function StatusPill({ status }: { status: api.FeedbackItem['status'] }) {
  const tone =
    status === 'new'     ? { bg: '#fef3c7', fg: '#854d0e' } :
    status === 'triaged' ? { bg: '#dbeafe', fg: '#1e40af' } :
    status === 'linked'  ? { bg: '#dcfce7', fg: '#166534' } :
                           { bg: '#f1f5f9', fg: '#475569' }
  return <span style={{ ...pillCat, background: tone.bg, color: tone.fg }}>{status}</span>
}

function Counter({ label, value, tone, muted }: { label: string; value: number; tone?: 'ok' | 'warn'; muted?: boolean }) {
  const color =
    muted        ? 'var(--color-text-mute)' :
    tone === 'warn' ? '#a16207' :
    tone === 'ok'   ? '#166534' :
                      'var(--color-text)'
  return (
    <div style={{ textAlign: 'center', minWidth: 64 }}>
      <div style={{ fontSize: 18, fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase', color: 'var(--color-text-mute)', marginTop: 2 }}>{label}</div>
    </div>
  )
}

function formatTimestamp(s: string): string {
  return formatUsTimestamp(s)
}

const headerRowStyle: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
  gap: 16, marginBottom: 16, flexWrap: 'wrap',
}
const h2Style: React.CSSProperties = { fontSize: 18, fontWeight: 600, margin: 0, display: 'inline-flex', alignItems: 'center', gap: 6 }
const subtitleStyle: React.CSSProperties = { margin: '4px 0 0', color: 'var(--color-text-soft)', fontSize: 13 }
const countsStyle: React.CSSProperties = { display: 'flex', gap: 14, padding: '10px 14px', background: 'var(--color-surface, #fff)', border: '1px solid var(--color-border)', borderRadius: 6 }
const filterRowStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }
const chipStyle: React.CSSProperties = {
  padding: '4px 10px', fontSize: 12,
  background: 'transparent', border: '1px solid var(--color-border)', borderRadius: 12,
  color: 'var(--color-text-soft)', cursor: 'pointer', fontFamily: 'inherit',
}
const chipActiveStyle: React.CSSProperties = { background: 'var(--color-text)', color: '#fff', borderColor: 'var(--color-text)' }
const listStyle: React.CSSProperties = { listStyle: 'none', margin: 0, padding: 0 }
const rowStyle: React.CSSProperties = {
  background: 'var(--color-surface, #fff)', border: '1px solid var(--color-border)',
  borderRadius: 8, padding: 14, marginBottom: 8,
}
const metaLineStyle: React.CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginBottom: 6 }
const pillCat: React.CSSProperties = {
  fontSize: 10, fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase',
  padding: '2px 8px', borderRadius: 10, background: '#f1f5f9', color: '#475569',
}
const pillScreen: React.CSSProperties = { ...pillCat, background: '#e0f2fe', color: '#075985', fontFamily: 'monospace', textTransform: 'none' as const }
const timeStyle: React.CSSProperties = { marginLeft: 'auto', fontSize: 11, color: 'var(--color-text-mute)' }
const bodyStyle: React.CSSProperties = { fontSize: 14, lineHeight: 1.55, margin: '6px 0', whiteSpace: 'pre-wrap' as const }
const subMetaStyle: React.CSSProperties = { display: 'flex', gap: 8, fontSize: 11, color: 'var(--color-text-mute)', flexWrap: 'wrap' }
const linkedStyle: React.CSSProperties = { fontSize: 12, color: '#166534', marginTop: 6 }
const notesStyle: React.CSSProperties = { fontSize: 12, color: 'var(--color-text-soft)', marginTop: 4, fontStyle: 'italic' as const }
const actionsStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4 }
const btnStyle: React.CSSProperties = {
  padding: '6px 12px', fontSize: 12, fontWeight: 500,
  background: 'var(--color-primary, #0d9488)', color: '#fff', border: 'none',
  borderRadius: 4, cursor: 'pointer', fontFamily: 'inherit',
  display: 'inline-flex', alignItems: 'center', gap: 4,
}
const btnStyleSecondary: React.CSSProperties = { ...btnStyle, background: 'transparent', color: 'var(--color-text-soft)', border: '1px solid var(--color-border)' }
const errorBox: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px',
  background: '#fee2e2', color: '#991b1b', borderRadius: 4, fontSize: 12, marginBottom: 8,
}
const loadingBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: 24, color: 'var(--color-text-soft)', fontSize: 14 }
const emptyBox: React.CSSProperties = {
  padding: 32, textAlign: 'center' as const, fontSize: 13, color: 'var(--color-text-mute)',
  background: 'var(--color-surface, #fff)', border: '1px dashed var(--color-border)', borderRadius: 8,
}
